import { logger } from '../lib/logger';
import { useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';

const STORAGE_LEAD_ID = 'gp_vsl_lead_id';
const STORAGE_VISITS = 'gp_visits';
const STORAGE_SECTIONS = 'gp_sections_explored';

const SECTION_IDS = ['roi', 'faq', 'media', 'benefits', 'thesis'];

const post = (signal, value) => {
  try {
    const lead_id = localStorage.getItem(STORAGE_LEAD_ID);
    if (!lead_id) return;
    return api.post('/public/track-signal', { lead_id, signal, value });
  } catch (err) {
    logger.debug('useScoringTracker.trackSignal failed', err);
    return null;
  }
};

/**
 * useScoringTracker
 * Centralised engagement tracker that runs once on the public landing.
 * - Tracks return-visit (one-shot, debounced via localStorage flag)
 * - Tracks 5-min on-page heartbeat
 * - Reports `section_explored` when an element with `data-track-section` enters viewport
 * - Returns `track(signal, value)` for ad-hoc events (chat started, secondary CTA click)
 */
export function useScoringTracker({ enabled = true } = {}) {
  const startTimeRef = useRef(Date.now());
  const reportedTimeRef = useRef(false);
  const reportedSectionsRef = useRef(new Set());

  // 1. Return-visit detection (one-shot per browser)
  useEffect(() => {
    if (!enabled) return;
    try {
      const visits = parseInt(localStorage.getItem(STORAGE_VISITS) || '0', 10) + 1;
      localStorage.setItem(STORAGE_VISITS, String(visits));
      if (visits >= 2) {
        post('return_visit', { visits });
      }
    } catch (err) { logger.warn('useScoringTracker:', err); }
  }, [enabled]);

  // 2. Time-on-page heartbeat (fire once at 5 minutes)
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      if (seconds >= 300 && !reportedTimeRef.current) {
        reportedTimeRef.current = true;
        post('time_on_page', { seconds });
      }
    }, 30000);
    return () => clearInterval(id);
  }, [enabled]);

  // 3. Section-in-viewport via IntersectionObserver
  useEffect(() => {
    if (!enabled) return;
    if (typeof IntersectionObserver === 'undefined') return;

    // Restore already-reported sections to avoid double-counting on the same browser
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_SECTIONS) || '[]');
      stored.forEach((s) => reportedSectionsRef.current.add(s));
    } catch (err) { logger.warn('useScoringTracker:', err); }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const section = entry.target.getAttribute('data-track-section');
          if (!section || !SECTION_IDS.includes(section)) return;
          if (reportedSectionsRef.current.has(section)) return;
          reportedSectionsRef.current.add(section);
          try {
            localStorage.setItem(
              STORAGE_SECTIONS,
              JSON.stringify(Array.from(reportedSectionsRef.current))
            );
          } catch (err) { logger.warn('useScoringTracker:', err); }
          post('section_explored', { section });
        });
      },
      { threshold: 0.35 }
    );

    // Observe everything currently on the page + run once after small delay
    // to catch elements rendered after first paint.
    const attach = () => {
      document.querySelectorAll('[data-track-section]').forEach((el) => obs.observe(el));
    };
    attach();
    const t = setTimeout(attach, 1500);

    return () => {
      clearTimeout(t);
      obs.disconnect();
    };
  }, [enabled]);

  // 4. Returns helper for ad-hoc reports
  const track = useCallback((signal, value) => post(signal, value), []);

  return { track };
}
